import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { Search, TrendingUp, Clock, Tag, Rss } from "lucide-react";

export default function Blog() {
  const [params, setParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [latest, setLatest] = useState([]);
  const [popular, setPopular] = useState([]);
  const [categories, setCategories] = useState({ available: [], counts: {} });
  const [q, setQ] = useState(params.get("q") || "");

  const category = params.get("category") || "";
  const tag = params.get("tag") || "";

  useEffect(() => {
    const qs = new URLSearchParams();
    if (category) qs.set("category", category);
    if (tag) qs.set("tag", tag);
    const search = params.get("q");
    if (search) qs.set("q", search);
    api.get(`/blog/posts?${qs.toString()}`).then(r => { setPosts(r.data.items); setTotal(r.data.total); });
  }, [category, tag, params]);

  useEffect(() => {
    api.get("/blog/latest?limit=4").then(r => setLatest(r.data));
    api.get("/blog/popular?limit=4").then(r => setPopular(r.data));
    api.get("/blog/categories").then(r => setCategories(r.data));
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    const np = new URLSearchParams(params);
    if (q) np.set("q", q); else np.delete("q");
    setParams(np);
  };

  return (
    <>
      <SEO title="Blog — Tech Alpha SSL" description="Insights, guides, and updates on SSL, security, hosting, and web performance." canonical="https://techalphassl.com/blog" />
      <section className="pt-20 pb-10">
        <div className="container-page max-w-4xl">
          <span className="eyebrow">The Blog</span>
          <h1 className="mt-4 text-4xl md:text-6xl font-semibold tracking-tighter">Insights on trust, speed & security.</h1>
          <p className="mt-4 text-lg text-muted-foreground">Deep dives from senior engineers.</p>
        </div>
      </section>

      <section className="pb-20">
        <div className="container-page grid gap-8 lg:grid-cols-[1fr_320px]">
          <div>
            <form onSubmit={submitSearch} className="flex gap-2 mb-6" data-testid="blog-search-form">
              <div className="relative flex-1">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles"
                  data-testid="blog-search-input"
                  className="w-full rounded-full border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 pl-10 pr-4 py-3 text-sm outline-none focus:border-[#1565FF]" />
              </div>
              <button className="btn-primary !py-2 !px-4 !text-sm" data-testid="blog-search-submit">Search</button>
            </form>

            {(category || tag) && (
              <div className="mb-6 text-sm text-muted-foreground">
                Filtered by {category && <span className="text-[#1565FF]">{category}</span>}{tag && <span className="text-[#1565FF]">#{tag}</span>}{" — "}
                <Link to="/blog" className="text-[#1565FF] underline">clear</Link>
              </div>
            )}

            <div className="grid gap-6">
              {posts.map(p => <PostCard key={p.id} post={p} />)}
              {posts.length === 0 && (
                <div className="card-elevated p-10 text-center text-muted-foreground">No posts found. Try a different search or category.</div>
              )}
            </div>
            {total > posts.length && (
              <p className="text-xs text-muted-foreground text-center mt-6">Showing {posts.length} of {total}</p>
            )}
          </div>

          <aside className="space-y-6">
            <SidebarCard title="Categories" icon={Tag}>
              <ul className="space-y-1.5">
                {(categories.available || []).map(c => (
                  <li key={c}>
                    <Link to={`/blog?category=${encodeURIComponent(c)}`} data-testid={`blog-category-${c.toLowerCase().replace(/\s+/g,'-')}`}
                      className="flex items-center justify-between text-sm text-foreground/80 hover:text-[#1565FF]">
                      <span>{c}</span>
                      <span className="text-xs text-muted-foreground">{categories.counts?.[c] || 0}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </SidebarCard>

            <SidebarCard title="Latest posts" icon={Clock}>
              <ul className="space-y-3">
                {latest.map(p => (
                  <li key={p.id}>
                    <Link to={`/blog/${p.slug}`} className="text-sm font-medium tracking-tight hover:text-[#1565FF] line-clamp-2">{p.title}</Link>
                    <div className="text-xs text-muted-foreground mt-0.5">{new Date(p.published_at).toLocaleDateString()}</div>
                  </li>
                ))}
                {latest.length === 0 && <li className="text-xs text-muted-foreground">No posts yet.</li>}
              </ul>
            </SidebarCard>

            <SidebarCard title="Popular" icon={TrendingUp}>
              <ul className="space-y-3">
                {popular.map(p => (
                  <li key={p.id}>
                    <Link to={`/blog/${p.slug}`} className="text-sm font-medium tracking-tight hover:text-[#1565FF] line-clamp-2">{p.title}</Link>
                    <div className="text-xs text-muted-foreground mt-0.5">{p.view_count || 0} views</div>
                  </li>
                ))}
                {popular.length === 0 && <li className="text-xs text-muted-foreground">No posts yet.</li>}
              </ul>
            </SidebarCard>

            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/blog/rss.xml`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-[#1565FF] hover:underline" data-testid="blog-rss-link">
              <Rss className="h-4 w-4"/> RSS feed
            </a>
          </aside>
        </div>
      </section>
    </>
  );
}

function PostCard({ post }) {
  return (
    <Link to={`/blog/${post.slug}`} className="card-elevated overflow-hidden hover:-translate-y-1 transition-transform block group" data-testid={`blog-post-card-${post.slug}`}>
      {post.featured_image && (
        <div className="h-48 overflow-hidden">
          <img src={post.featured_image} alt={post.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        </div>
      )}
      <div className="p-6">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="rounded-full bg-[#1565FF]/10 text-[#1565FF] px-2 py-0.5">{post.category}</span>
          <span>·</span>
          <span>{new Date(post.published_at).toLocaleDateString()}</span>
        </div>
        <h3 className="mt-3 text-xl font-semibold tracking-tight group-hover:text-[#1565FF] transition-colors">{post.title}</h3>
        {post.excerpt && <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{post.excerpt}</p>}
        <div className="mt-4 text-xs text-muted-foreground">By {post.author}</div>
      </div>
    </Link>
  );
}

function SidebarCard({ title, icon: Icon, children }) {
  return (
    <div className="card-elevated p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="h-4 w-4 text-[#1565FF] dark:text-[#4FC3F7]"/>
        <h3 className="text-sm font-semibold tracking-tight">{title}</h3>
      </div>
      {children}
    </div>
  );
}
